import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getCRList, deleteCR } from '../services/api';
import { useAuth } from '../App';

const TYPE_BADGE = { CLEARED: 'badge-green', FAILED: 'badge-red', VT: 'badge-blue', SAV: 'badge-amber', CRI: 'badge-gray' };
const STATUS_BADGE = { pending: 'badge-amber', validated: 'badge-green', rejected: 'badge-red' };
const STATUS_LABEL = { pending: 'En attente', validated: 'Validé', rejected: 'Rejeté' };

export default function CompteRendus({ onPendingCount }) {
  const navigate = useNavigate();
  const { admin } = useAuth();
  const isAdmin = admin?.role === 'admin';
  const [searchParams] = useSearchParams();
  const [crs, setCRs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(searchParams.get('filter') || 'pending');
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [typeFilter, setTypeFilter] = useState('all');
  const [techFilter, setTechFilter] = useState('all');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [msg, setMsg] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const data = await getCRList({ status: filter !== 'all' ? filter : undefined });
      setCRs(data.crs || []);
      const pending = (data.crs || []).filter(c => c.status === 'pending').length;
      onPendingCount?.(pending);
    } catch {
      setCRs([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filter]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteCR(confirmDelete);
      setMsg('Compte-rendu supprimé');
      setConfirmDelete(null);
      load();
    } catch {
      setMsg('Erreur lors de la suppression');
    } finally { setDeleting(false); }
  };

  const handleExport = () => {
    const params = new URLSearchParams();
    if (filter !== 'all') params.set('status', filter);
    if (techFilter !== 'all') params.set('technicien', techFilter);
    if (typeFilter !== 'all') params.set('type_cr', typeFilter);
    const token = localStorage.getItem('admin_token');
    fetch(`/api/admin/export-csv?${params}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `CRs_export_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      });
  };

  // Liste techniciens pour le filtre
  const techniciens = [...new Set(crs.map(c => c.technicien_nom).filter(Boolean))].sort();

  const filtered = crs.filter(cr => {
    const matchSearch = `${cr.ref_commande} ${cr.ref_work_order} ${cr.technicien_nom} ${cr.client_final}`.toLowerCase().includes(search.toLowerCase());
    const matchType = typeFilter === 'all' || cr.type_cr === typeFilter || cr.resultat === typeFilter;
    const matchTech = techFilter === 'all' || cr.technicien_nom === techFilter;
    return matchSearch && matchType && matchTech;
  }).sort((a, b) => {
    // En attente : du plus ancien (haut) au plus récent (bas)
    if (filter === 'pending') {
      return new Date(a.date_soumission || a.date_creation || 0) - new Date(b.date_soumission || b.date_creation || 0);
    }
    return new Date(b.date_soumission || b.date_creation || 0) - new Date(a.date_soumission || a.date_creation || 0);
  });

  const crToDelete = confirmDelete ? filtered.find(c => c.id === confirmDelete) || crs.find(c => c.id === confirmDelete) : null;

  return (
    <>
      <div className="topbar">
        <div className="topbar-title">Comptes-rendus</div>
        <div className="topbar-actions">
          <input className="form-input" style={{ width: 180 }} placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)} />
          <select className="form-input form-select" style={{ width: 140 }} value={techFilter} onChange={e => setTechFilter(e.target.value)}>
            <option value="all">Tous techniciens</option>
            {techniciens.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select className="form-input form-select" style={{ width: 130 }} value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
            <option value="all">Tous types</option>
            <option value="CRI">CRI</option>
            <option value="CLEARED">CLEARED</option>
            <option value="FAILED">FAILED</option>
            <option value="VT">VT</option>
            <option value="SAV">SAV</option>
          </select>
          <button className="btn btn-secondary btn-sm" onClick={handleExport} title="Exporter en CSV">⬇ CSV</button>
        </div>
      </div>
      <div className="page">

        {msg && <div className={`alert ${msg.includes('supprimé') ? 'alert-success' : 'alert-error'}`} onClick={() => setMsg('')}>{msg} ✕</div>}

        {/* Filtres statut */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          {[['all', 'Tous'], ['pending', 'En attente'], ['validated', 'Validés'], ['rejected', 'Rejetés']].map(([v, l]) => (
            <button key={v} onClick={() => setFilter(v)} className="btn" style={{ background: filter === v ? 'var(--accent)' : 'var(--bg3)', color: filter === v ? '#fff' : 'var(--text2)', border: '1px solid ' + (filter === v ? 'var(--accent)' : 'var(--border)') }}>
              {l}
            </button>
          ))}
          <div style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text3)', alignSelf: 'center' }}>
            {filtered.length} résultat{filtered.length !== 1 ? 's' : ''}
          </div>
        </div>

        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Référence</th>
                  <th>Technicien</th>
                  <th>Réf commande</th>
                  <th>Réf Work Order</th>
                  <th>TYPE CR</th>
                  <th>Raccordement</th>
                  <th>Devis</th>
                  <th>Résultat</th>
                  <th>Date</th>
                  <th>Heure</th>
                  <th>Statut</th>
                  <th>État commande</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={13} style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>Chargement…</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={13} style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>Aucun compte-rendu</td></tr>
                ) : filtered.map(cr => (
                  <tr key={cr.id}>
                    <td><span className="mono" style={{ fontSize: 11 }}>{cr.dossier_id || cr.id}</span></td>
                    <td style={{ fontWeight: 500 }}>{cr.technicien_nom}</td>
                    <td><span className="mono" style={{ fontSize: 11, color: 'var(--blue2)' }}>{cr.ref_commande || '—'}</span></td>
                    <td><span className="mono" style={{ fontSize: 11, color: 'var(--text2)' }}>{cr.ref_work_order || '—'}</span></td>
                    <td><span className={`badge ${TYPE_BADGE[cr.type_cr] || 'badge-gray'}`}>{cr.type_cr || '—'}</span></td>
                    <td><span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)' }}>{cr.type_raccordement || '—'}</span></td>
                    <td>
                      {cr.devis_xlsx_url
                        ? <span className="badge" style={{ background: '#1D6F42', color: '#fff' }}>📊 Devis</span>
                        : cr.devis_forfaitaire
                          ? <span className="badge" style={{ background: '#854F0B', color: '#fff' }}>✓ Devis</span>
                          : <span style={{ color: 'var(--text3)', fontSize: 12 }}>—</span>}
                    </td>
                    <td><span className={`badge ${cr.resultat === 'CLEARED' ? 'badge-green' : cr.resultat === 'FAILED' ? 'badge-red' : 'badge-gray'}`}>{cr.resultat || '—'}</span></td>
                    <td><span style={{ color: 'var(--text3)', fontSize: 12 }}>{(cr.date_soumission || cr.date_creation) ? new Date(cr.date_soumission || cr.date_creation).toLocaleDateString('fr-FR') : '—'}</span></td>
                    <td><span style={{ color: 'var(--text3)', fontSize: 12 }}>{(cr.date_soumission || cr.date_creation) ? new Date(cr.date_soumission || cr.date_creation).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '—'}</span></td>
                    <td>
                      <div>
                        <span className={`badge ${STATUS_BADGE[cr.status] || 'badge-gray'}`}><span className="dot" />{STATUS_LABEL[cr.status] || cr.status}</span>
                        {cr.status === 'rejected' && cr.motif_rejet && (
                          <div style={{ fontSize: 10, color: '#ff6b6b', marginTop: 3, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={cr.motif_rejet}>
                            ↳ {cr.motif_rejet}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div style={{ fontSize: 12, color: 'var(--text2)', maxWidth: 170 }}>
                        {cr.etape_commande || '—'}
                        {cr.cr_sur_ce_wo > 1 && (
                          <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>
                            {cr.cr_sur_ce_wo} CR déposés sur ce WO
                          </div>
                        )}
                        {cr.alerte_etape && (
                          <div title={cr.alerte_etape}
                            style={{ fontSize: 10, fontWeight: 700, color: '#b45309', marginTop: 3, lineHeight: 1.3 }}>
                            ⚠ {cr.alerte_etape}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/cr/${cr.id}`)}>Voir →</button>
                        {isAdmin && <button className="btn btn-danger btn-sm" onClick={() => setConfirmDelete(cr.id)} title="Supprimer">🗑</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Modal confirmation suppression */}
        {confirmDelete && (
          <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setConfirmDelete(null)}>
            <div className="modal">
              <div className="modal-header">
                <div className="modal-title">Confirmer la suppression</div>
                <button className="btn btn-ghost btn-sm" onClick={() => setConfirmDelete(null)}>✕</button>
              </div>
              <div className="modal-body">
                <p style={{ color: 'var(--text2)', marginBottom: 12 }}>
                  Tu es sur le point de supprimer définitivement ce compte-rendu :
                </p>
                <div style={{ background: 'var(--bg3)', borderRadius: 8, padding: '12px 14px', marginBottom: 12 }}>
                  <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--blue2)', marginBottom: 4 }}>
                    {crToDelete?.ref_commande || crToDelete?.dossier_id || confirmDelete}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text2)' }}>
                    {crToDelete?.technicien_nom} · {crToDelete?.type_cr} · {crToDelete?.resultat || '—'}
                  </div>
                </div>
                <div style={{ background: 'rgba(212,43,43,0.1)', border: '1px solid rgba(212,43,43,0.2)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#ff6b6b' }}>
                  ⚠️ Cette action est irréversible. Le PDF associé sera également supprimé.
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setConfirmDelete(null)}>Annuler</button>
                <button className="btn btn-danger" onClick={handleDelete} disabled={deleting}>
                  {deleting ? '...' : '🗑 Supprimer définitivement'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
